/**
 * Google Ads conversion tracking helpers (gtag.js).
 *
 * The tag is loaded site-wide from `__root.tsx`'s head — deliberately, per
 * Google's recommended setup: it builds remarketing audiences on the
 * marketing surface, keeps Google's tag-detection checks green, and means
 * future in-app conversion events (e.g. subscription started) are a
 * one-line `gtag('event', ...)`. It only renders when `VITE_GOOGLE_ADS_ID`
 * is set at build time, so dev and preview builds ship no tracking.
 *
 * Setup, env vars, and the Ads-UI side: docs/ads/conversion-tracking.md
 */

import { claimSignUpReport, isFreshSignUp } from './signup-detection'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

/** Google tag id (`AW-…`). Only set in production deploys. */
export const GOOGLE_ADS_ID: string | undefined = import.meta.env.VITE_GOOGLE_ADS_ID

/** Sign-up conversion `send_to` label (`AW-…/…`). */
const SIGNUP_CONVERSION_LABEL: string | undefined = import.meta.env.VITE_GOOGLE_ADS_SIGNUP_LABEL

/** Activation conversion `send_to` label (`AW-…/…`) — see `maybeFireActivationConversion`. */
const ACTIVATION_CONVERSION_LABEL: string | undefined = import.meta.env
  .VITE_GOOGLE_ADS_ACTIVATION_LABEL

/** localStorage key remembering which user already had the sign-up conversion sent. */
const FIRED_KEY = 'planno-ads-signup-conversion'

/** localStorage key remembering that this browser already reported an activation. */
const ACTIVATION_FIRED_KEY = 'planno-ads-activation-conversion'

/**
 * Reports the sign-up conversion to Google Ads, at most once per user per
 * browser. Safe to call on every authenticated mount: it no-ops unless the
 * tag is configured, the user was created within the last 30 minutes, and
 * this browser hasn't already reported them.
 */
export const maybeFireSignUpConversion = (user: {
  _id: string
  _creationTime: number
  email?: string
}) => {
  if (!(GOOGLE_ADS_ID && SIGNUP_CONVERSION_LABEL) || typeof window === 'undefined') {
    return
  }
  const { gtag } = window
  if (!gtag) {
    return
  }
  if (!isFreshSignUp(user._creationTime)) {
    return
  }
  // On a storage failure (private mode) this reports again; Google's own
  // click-level dedupe makes an occasional double-count harmless.
  if (!claimSignUpReport(FIRED_KEY, user._id)) {
    return
  }
  // Enhanced conversions ("conversões otimizadas"): gtag normalizes and
  // SHA-256-hashes the email client-side before anything leaves the browser.
  // Google only consumes it once the feature is toggled on for the
  // conversion action in the Ads UI — until then it's inert.
  if (user.email) {
    gtag('set', 'user_data', { email: user.email.trim().toLowerCase() })
  }
  gtag('event', 'conversion', { send_to: SIGNUP_CONVERSION_LABEL })
}

/**
 * Reports the *activation* conversion — the user created their first project.
 *
 * Exists because a sign-up is too cheap a signal: the Aug 2026 review found
 * Google happily buying sign-ups from people looking for a rendering AI or a
 * free floor-plan drawer, who never get past the welcome modal. Creating a
 * project is what every engaged ad-cohort account did and the drive-bys never
 * did, so this is the conversion the campaign should be optimised toward.
 *
 * Fired from the project-create flow. Once per browser (localStorage guard);
 * there is deliberately no account-age window — Google attributes the event
 * to the ad click within its own conversion window, and a click-less browser
 * firing it is simply ignored on Google's side. Wired to its own Ads
 * conversion action; see docs/ads/conversion-tracking.md.
 */
export const maybeFireActivationConversion = () => {
  if (!(GOOGLE_ADS_ID && ACTIVATION_CONVERSION_LABEL) || typeof window === 'undefined') {
    return
  }
  const { gtag } = window
  if (!gtag) {
    return
  }
  try {
    if (localStorage.getItem(ACTIVATION_FIRED_KEY) === '1') {
      return
    }
    localStorage.setItem(ACTIVATION_FIRED_KEY, '1')
  } catch {
    // localStorage unavailable — report anyway; Google's click-level dedupe
    // ("Contagem: Uma") makes an occasional repeat harmless.
  }
  gtag('event', 'conversion', { send_to: ACTIVATION_CONVERSION_LABEL })
}
